import { useEffect, useState } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { FaLocationArrow } from "react-icons/fa6";
import { HiChevronDown } from "react-icons/hi";
import { twMerge } from "tailwind-merge";

import { SvButton, SvText } from "@/components";
import { useGeoBoundaryQuery } from "@/data/queries/RepresentQueries.ts";
import { useRidingsQuery } from "@/data/queries/RidingQueries.ts";
import { RidingDataType } from "@/domain/models.tsx";

type RidingSelectorBlockProps = {
  election: string;
  riding?: string;
};

export const RidingSelectorBlock = ({
  election,
  riding,
}: RidingSelectorBlockProps) => {
  const { data: ridingsData } = useRidingsQuery(election);
  const { t } = useTranslation();

  const [selectedRiding, setSelectedRiding] = useState<RidingDataType | null>(
    ridingsData?.find(r => r.ridingNumber === riding) ?? null,
  );
  const [query, setQuery] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<GeolocationPosition | null>(
    null,
  );
  const navigate = useNavigate();

  const { data: geoRidingId, refetch: refetchGeoBoundary } =
    useGeoBoundaryQuery(
      userLocation?.coords.latitude,
      userLocation?.coords.longitude,
      Boolean(userLocation),
    );

  const filteredRidings = ridingsData?.filter(riding =>
    riding.ridingName.toLowerCase().includes(query?.toLowerCase() ?? ""),
  );

  const onRidingSelected = (riding: RidingDataType) => {
    setSelectedRiding(riding);
    setQuery("");
    void navigate({
      to: "/ridings/$election/$riding",
      params: { election, riding: riding.ridingNumber },
    });
  };

  function getUserLocation() {
    if (!navigator.geolocation) {
      throw new Error("Geolocation is not supported by your browser");
    } else {
      navigator.geolocation.getCurrentPosition(
        geo => setUserLocation(geo),
        e => {
          console.error(e);
        },
      );
    }
  }

  useEffect(() => {
    if (userLocation) {
      void refetchGeoBoundary();
    }
  }, [refetchGeoBoundary, userLocation]);

  useEffect(() => {
    if (geoRidingId) {
      void navigate({
        to: "/ridings/$election/$riding",
        params: { election, riding: geoRidingId },
      });
    }
  }, [navigate, election, geoRidingId]);

  return (
    <div className="bg-orange-50 py-16">
      <div className="container flex flex-col items-center justify-center gap-4 text-center">
        <SvText
          variant="title"
          className="text-dark text-3xl font-extrabold"
          i18nKey="ridings.title"
          i18nValues={{ area: "Canada" }}
        />
        <p>{t("ridings.subtitle1")}</p>
        <p>{t("ridings.subtitle2")}</p>
        <div className="mt-5 flex w-full flex-col items-center justify-center gap-4 lg:flex-row lg:items-stretch lg:gap-6">
          <Combobox
            immediate
            value={selectedRiding}
            onChange={onRidingSelected}
            onClose={() => setQuery("")}>
            <div className="group relative w-full max-w-lg">
              <ComboboxInput
                placeholder={t("ridings.select_placeholder")}
                className={twMerge(
                  "border-ind-grey-600 w-full cursor-pointer rounded-md border bg-white py-3 pr-8 pl-4 text-sm font-medium focus:cursor-text",
                  "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25",
                )}
                displayValue={(riding: RidingDataType) => riding?.ridingName}
                onChange={event => setQuery(event.target.value)}
              />
              <ComboboxButton className="group absolute inset-y-0 right-0 cursor-pointer px-2.5">
                <HiChevronDown className="text-ind-grey fill-ind-grey group-hover:fill-primary hover:fill-primary size-4 transition-transform group-data-[open]:-rotate-180" />
              </ComboboxButton>
            </div>
            <ComboboxOptions
              anchor="bottom"
              transition
              className={twMerge(
                "w-[var(--input-width)] rounded-xl border border-white/5 bg-white p-1 [--anchor-gap:var(--spacing-1)] empty:invisible",
                "border-ind-grey-40 border shadow-[0_4px_20px_0_rgba(0,0,0,0.1)] transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0",
              )}>
              {filteredRidings?.map(riding => (
                <ComboboxOption
                  key={riding.ridingNumber}
                  value={riding}
                  className="flex cursor-default items-center gap-2 rounded-lg px-3 py-1.5 select-none data-[focus]:bg-white/10">
                  {riding.ridingName}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          </Combobox>
          <SvText
            i18nKey="common.or"
            className="hidden self-center align-middle lg:block"
          />
          <SvButton
            color="secondary"
            i18nKey="ridings.use_location"
            iconLeft={<FaLocationArrow />}
            onClick={getUserLocation}
            className=""
          />
        </div>
        <a
          href="https://www.elections.ca/Scripts/vis/FindED?L=e&EV=88&QID=-1&PAGEID=21"
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:sketch-underline-primary-300 mb-5 px-2 font-medium">
          <Trans i18nKey="ridings.not_sure" />
        </a>
        <Outlet />
      </div>
    </div>
  );
};
